import { ApolloClient, } from '@apollo/client/core/index.js';
import { Subject } from 'rxjs';
import { defaultClientOptions, mergeClientOptions } from '../common/index.js';
import { BaseApiConstants } from './base-api.constants.js';
/**
 * The base for a client which connectes to the Apigee API service.
 */
export class BaseApiClient {
    _clientOptions = defaultClientOptions;
    _typeDefs;
    _fragments = '';
    _variables = {};
    endpoint;
    apikey;
    apiSubject = new Subject();
    timeOutSet = false;
    timeOutSetting = 300;
    apolloClient;
    constructor(endpoint, apikey, clientOptions) {
        const ep = endpoint?.toString();
        this.testEndpointAndApikey(ep, apikey);
        this.endpoint = ep;
        this.apikey = apikey;
        this.setClientOptions(clientOptions);
        if (!this.clientOptions.ssrMode) {
            this.createClient();
        }
    }
    /**
     * Set endpoint and apikey variables, throwing errors
     *  if either are missing
     */
    testEndpointAndApikey(endpoint, apikey) {
        if ((!endpoint || endpoint === '') && (!apikey || apikey === '')) {
            throw new Error(BaseApiConstants.LOG_REQUIRED_BOTH);
        }
        if (!endpoint) {
            throw new Error(BaseApiConstants.LOG_REQUIRED_ENDPOINT);
        }
        if (!apikey) {
            throw new Error(BaseApiConstants.LOG_REQUIRED_APIKEY);
        }
    }
    /**
     * Sets initial client options object
     *
     * note: need logger to replace console.warn:
     * @rally https://rally1.rallydev.com/#/?detail=/userstory/816373085855&fdp=true
     */
    setClientOptions(initClientOptions) {
        if (!initClientOptions?.typeDefs || initClientOptions?.typeDefs === '') {
            console.warn(BaseApiConstants.LOG_WARN_TYPEDEFS);
        }
        else {
            this.typeDefs = initClientOptions?.typeDefs;
        }
        this.clientOptions = initClientOptions || {};
    }
    /**
     * Set the typedefs variable
     */
    set typeDefs(newTypeDefs) {
        if (newTypeDefs && newTypeDefs !== '') {
            this._typeDefs = newTypeDefs;
        }
    }
    /**
     * Get the typedefs variable content
     */
    get typeDefs() {
        return this._typeDefs;
    }
    /**
     * Ensure new fragment content and set the fragments variable
     */
    set fragments(newFragments) {
        if (!newFragments || newFragments === '') {
            return;
        }
        this._fragments = newFragments;
    }
    /**
     * Get the fragments content
     */
    get fragments() {
        return this._fragments;
    }
    /**
     * Set the query variables
     */
    set variables(vars) {
        if (vars && !Array.isArray(vars) && typeof vars === 'object') {
            this._variables = vars;
        }
    }
    /**
     * Get the query variables
     */
    get variables() {
        return this._variables;
    }
    /**
     * Set the non-immutable content of the Apollo client options
     */
    set clientOptions(newOptions) {
        const immutable = {
            uri: this.endpoint,
            headers: {
                apikey: this.apikey,
            },
            typeDefs: this.typeDefs,
        };
        this._clientOptions = mergeClientOptions(this._clientOptions, newOptions, immutable);
    }
    /**
     * Retrieve the clientOptions via this.clientOptions
     */
    get clientOptions() {
        return this._clientOptions;
    }
    /**
     * Creates a new ApolloClient instance and adds
     *  it to `this.apolloClient`
     */
    createClient() {
        if (this.apolloClient) {
            this.apolloClient.stop();
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        this.apolloClient = new ApolloClient(this.clientOptions);
    }
    /* v8 ignore start */
    /**
     * Override with method that returns a gql`` query which is
     *  updated to include any `fragments` changes
     *
     * @example
     * return gql`query getSchema{
     *  types {
     *    ${this.fragments}
     *  }
     * }`;
     */
    getQuery() {
        // Override
    }
    /**
     * Separate method to process the response from the apigee/graphQL server.
     *  The server always returns either `data` or `errors`, so the args on this
     *  method should always be { data, errors }: ApolloQueryResult<Your_Endpoint_Type>
     *
     * @example
     * override processResponse({ data, errors }: ApolloQueryResult<MyType>) {
     *  if (errors) {
     *    throw new Error(`API Response error: ${errors}`);
     *  }
     *
     *  // REQUIRED: sends the data to the subscriber
     *  this.apiSubject?.next(data);
     */
    processResponse({ data, errors }) {
        // Override
    }
    /**
     * Override with method that adds an `Observable` via
     *  watchQuery onto this.apiClient. Should process new
     *  fragments which control the returned content in the GraphQL
     *  response. The extended client/service should use `processResponse`
     *  on the response as that makes it easier to test and manage
     *
     * @example
     * override updateRequest(newFragments = '') {
     *  // sets the new fragments
     *  this.fragments = newFragments;
     *  // Creates the Observable using the query from this.getQuery
     *  const apiQueryObservable = this.apolloClient.watchQuery({
     *    // query incorporates new fragments
     *    query: this.getQuery(),
     *    // ONLY for variables required to make query,
     *    // e.g. `ticker` when query is getSchema($ticker: String!)
     *    variables: this.variables,
     *  });
     *
     *  // sets up response subscription, sends it to be processed
     *  apiQueryObservable.subscribe(this.processResponse);
     * }
     */
    updateRequest(...args) {
        // Override
    }
    /* v8 ignore end */
    fetch() {
        if (!this.apolloClient) {
            this.createClient();
        }
        return this.apiSubject.asObservable();
    }
}
//# sourceMappingURL=base-api.client.js.map